import React, { useState } from 'react';
import { Card, Table, Steps, Tag, Button, Space, Drawer, Descriptions, Typography, Progress, Alert, Modal, message } from 'antd';
import {
  EyeOutlined,
  StopOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Step } = Steps;

const STEP_STATUS_MAP = {
  PENDING: 'wait',
  RUNNING: 'process',
  SUCCESS: 'finish',
  FAILED: 'error',
  SKIPPED: 'wait',
};

const WorkflowExecutionView = ({ workflows, onViewDetails, onCancelWorkflow, onDeleteWorkflow, onRunWorkflow, isLoading }) => {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const WORKFLOW_STATUS = {
    RUNNING: { color: 'processing', icon: <SyncOutlined spin />, label: pa('enabled') },
    SUCCESS: { color: 'success', icon: <CheckCircleOutlined />, label: pa('statusCompleted') },
    FAILED: { color: 'error', icon: <CloseCircleOutlined />, label: pa('workflowFailed') },
    CANCELLED: { color: 'default', icon: <StopOutlined />, label: pa('statusCancelled') },
    PENDING: { color: 'default', icon: <ClockCircleOutlined />, label: pa('statusDraft') },
  };

  const handleViewDetails = (workflow) => {
    setSelectedWorkflow(workflow);
    setDrawerVisible(true);
    if (onViewDetails) {
      onViewDetails(workflow._id);
    }
  };

  const handleCancelWorkflow = (workflowId) => {
    Modal.confirm({
      title: pa('cancelWorkflow'),
      content: pa('deactivateCompanyDesc'),
      okText: pa('yes'),
      cancelText: pa('no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await onCancelWorkflow(workflowId);
          message.success(t('operationSuccess', { ns: 'messages' }));
          setDrawerVisible(false);
        } catch {
          message.error(t('operationError', { ns: 'messages' }));
        }
      },
    });
  };

  const handleDeleteWorkflow = (workflowId) => {
    Modal.confirm({
      title: pa('deleteWorkflow'),
      content: pa('deleteWorkflowDesc'),
      okText: pa('delete'),
      cancelText: pa('cancel'),
      okType: 'danger',
      onOk: async () => {
        try {
          await onDeleteWorkflow(workflowId);
          message.success(pa('workflowDeleted'));
          setDrawerVisible(false);
        } catch {
          message.error(t('operationError', { ns: 'messages' }));
        }
      },
    });
  };

  const calculateProgress = (steps) => {
    if (!steps || steps.length === 0) return 0;
    const completedSteps = steps.filter(s => s.status === 'SUCCESS' || s.status === 'FAILED').length;
    return Math.round((completedSteps / steps.length) * 100);
  };

  const columns = [
    {
      title: pa('workflows'),
      dataIndex: 'workflowName',
      key: 'workflowName',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: t('company', { ns: 'tables' }),
      dataIndex: ['company', 'name'],
      key: 'company',
      render: (name) => name || '-',
    },
    {
      title: pa('period'),
      key: 'period',
      render: (_, record) => (
        <Tag color="blue">
          {record.month}/{record.year}
        </Tag>
      ),
    },
    {
      title: pa('startedAt'),
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (startedAt) => (
        <div>
          <div>{dayjs(startedAt).format('DD.MM.YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(startedAt).format('HH:mm:ss')}
          </Text>
        </div>
      ),
    },
    {
      title: pa('progressAndDuration'),
      key: 'progress',
      width: 200,
      render: (_, record) => {
        const progress = calculateProgress(record.steps);

        // Calculate duration
        let duration = '-';
        if (record.completedAt) {
          const durationMs = dayjs(record.completedAt).diff(dayjs(record.startedAt));
          const seconds = Math.floor(durationMs / 1000);
          const minutes = Math.floor(seconds / 60);
          const remainingSeconds = seconds % 60;

          if (minutes > 0) {
            duration = `${minutes}m ${remainingSeconds}s`;
          } else {
            duration = `${seconds}s`;
          }
        } else if (record.startedAt && record.status === 'RUNNING') {
          const durationMs = dayjs().diff(dayjs(record.startedAt));
          const seconds = Math.floor(durationMs / 1000);
          const minutes = Math.floor(seconds / 60);
          const remainingSeconds = seconds % 60;

          if (minutes > 0) {
            duration = `${minutes}m ${remainingSeconds}s`;
          } else {
            duration = `${seconds}s`;
          }
        }

        return (
          <div>
            <Progress
              percent={progress}
              size="small"
              status={record.status === 'FAILED' ? 'exception' : record.status === 'SUCCESS' ? 'success' : 'active'}
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {pa('duration')}: {duration}
            </Text>
          </div>
        );
      },
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = WORKFLOW_STATUS[status] || WORKFLOW_STATUS.PENDING;
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            {pa('view')}
          </Button>
          {record.status === 'RUNNING' ? (
            <Button
              type="link"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancelWorkflow(record._id)}
            >
              {pa('cancel')}
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteWorkflow(record._id)}
            >
              {pa('delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={pa('workflowExecutions')}
        extra={
          <Button
            type="primary"
            className="btn-primary"
            icon={<PlayCircleOutlined />}
            onClick={onRunWorkflow}
          >
            {pa('runMonthlyPayroll')}
          </Button>
        }
        style={{ marginBottom: 24 }}
        loading={isLoading}
      >
        <Table
          dataSource={workflows}
          columns={columns}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Workflow Details Drawer */}
      <Drawer
        title={pa('workflowDetails')}
        placement="right"
        width={900}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        extra={
          selectedWorkflow?.status === 'RUNNING' && (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancelWorkflow(selectedWorkflow._id)}
            >
              {pa('cancelWorkflow')}
            </Button>
          )
        }
      >
        {selectedWorkflow && (
          <div>
            {selectedWorkflow.status === 'FAILED' && (
              <Alert
                message={pa('workflowFailed')}
                description={selectedWorkflow.error?.message || pa('errorMessage')}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {selectedWorkflow.status === 'SUCCESS' && (
              <Alert
                message={pa('statusCompleted')}
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {selectedWorkflow.status === 'RUNNING' && (
              <Alert
                message={pa('enabled')}
                type="info"
                showIcon
                icon={<SyncOutlined spin />}
                style={{ marginBottom: 16 }}
              />
            )}

            <Descriptions title={pa('workflowDetails')} bordered column={2} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="ID" span={2}>
                {selectedWorkflow._id}
              </Descriptions.Item>
              <Descriptions.Item label={pa('workflows')}>
                {selectedWorkflow.workflowName}
              </Descriptions.Item>
              <Descriptions.Item label={t('status', { ns: 'tables' })}>
                <Tag icon={WORKFLOW_STATUS[selectedWorkflow.status]?.icon} color={WORKFLOW_STATUS[selectedWorkflow.status]?.color}>
                  {WORKFLOW_STATUS[selectedWorkflow.status]?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={pa('period')}>
                {selectedWorkflow.month}/{selectedWorkflow.year}
              </Descriptions.Item>
              <Descriptions.Item label={t('company', { ns: 'tables' })}>
                {selectedWorkflow.company?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={pa('startedAt')}>
                {dayjs(selectedWorkflow.startedAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label={pa('completedAt')}>
                {selectedWorkflow.completedAt
                  ? dayjs(selectedWorkflow.completedAt).format('DD.MM.YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={pa('duration')} span={2}>
                {selectedWorkflow.completedAt
                  ? `${dayjs(selectedWorkflow.completedAt).diff(dayjs(selectedWorkflow.startedAt), 'second')}s`
                  : `${dayjs().diff(dayjs(selectedWorkflow.startedAt), 'second')}s`}
              </Descriptions.Item>
            </Descriptions>

            {selectedWorkflow.steps && selectedWorkflow.steps.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{pa('executionLogs')}</Title>
                <Steps
                  direction="vertical"
                  current={selectedWorkflow.steps.findIndex(s => s.status === 'RUNNING')}
                  style={{ marginTop: 16 }}
                >
                  {selectedWorkflow.steps.map((step, index) => {
                    const status = STEP_STATUS_MAP[step.status] || 'wait';
                    return (
                      <Step
                        key={index}
                        title={step.name}
                        status={status}
                        description={
                          <div>
                            <Tag color={WORKFLOW_STATUS[step.status]?.color || 'default'}>
                              {step.status}
                            </Tag>
                            {step.startTime && (
                              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                {dayjs(step.startTime).format('HH:mm:ss')}
                              </Text>
                            )}
                            {step.error && (
                              <Alert
                                message={step.error.message}
                                type="error"
                                size="small"
                                showIcon
                                style={{ marginTop: 8 }}
                              />
                            )}
                          </div>
                        }
                      />
                    );
                  })}
                </Steps>
              </div>
            )}

            {selectedWorkflow.error && (
              <div>
                <Title level={5}>{pa('errorMessage')}</Title>
                <Alert
                  message={selectedWorkflow.error.message}
                  type="error"
                  showIcon
                />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default WorkflowExecutionView;
